<script setup>
import { inject } from 'vue'
const rescheduleData = inject('rescheduleData', null)
</script>

<!-- Skeleton mirrors the exact three-zone HostPanel structure -->
<template>
  <div class="flex-grow bg-white dark:bg-[#1C2128] md:rounded-lg md:shadow-lg
              overflow-hidden w-full md:h-[99.99%] animate-pulse">
    <div class="w-full md:flex h-full">

      <!-- ── Left panel (2/5) — three-zone layout ──────────────────────── -->
      <div class="w-full md:w-2/5 flex flex-col border-b md:border-b-0 md:border-r
                  border-gray-200 dark:border-gray-500 overflow-hidden">

        <!-- TOP: banner + avatar + name + divider -->
        <div class="flex-shrink-0">

          <!-- Banner -->
          <div class="h-32 bg-gray-200 dark:bg-gray-700 w-full" />

          <!-- Avatar -->
          <div class="flex justify-center -mt-12 relative z-10">
            <div class="p-0.5 bg-white dark:bg-[#1C2128] rounded-full">
              <div class="h-24 w-24 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
          </div>

          <!-- Name + designation -->
          <div class="flex flex-col items-center gap-2 mt-2 px-5">
            <div class="h-5 w-36 bg-gray-200 dark:bg-gray-700 rounded" />
            <div class="h-3.5 w-24 bg-gray-100 dark:bg-gray-800 rounded" />
          </div>

          <!-- Social icons row -->
          <div class="flex justify-center gap-2 mt-2 px-5">
            <div v-for="i in 3" :key="i" class="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>

          <!-- Divider -->
          <div class="mt-3 border-t border-gray-100 dark:border-gray-700/60" />
        </div>

        <!-- MIDDLE: scrollable event details -->
        <div class="flex-1 min-h-0 overflow-hidden px-5 py-4 space-y-4">

          <!-- Event section label + name card -->
          <div>
            <div class="flex items-center justify-between mb-3">
              <div class="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded" />
              <div class="md:hidden h-3.5 w-3.5 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div class="rounded-[5px] bg-gray-100 dark:bg-gray-700/60 px-4 py-3 mb-4">
              <div class="h-4 w-3/4 bg-gray-200 dark:bg-gray-600 rounded" />
            </div>

            <!-- Detail rows: icon + text -->
            <div class="space-y-3">
              <div v-for="(w, i) in ['w-16', 'w-28', 'w-40']" :key="i"
                   class="flex items-center gap-2.5">
                <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div class="h-3.5 bg-gray-100 dark:bg-gray-800 rounded" :class="w" />
              </div>
            </div>
          </div>

          <!-- Former Time section (reschedule only) -->
          <div v-if="rescheduleData">
            <div class="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
            <div class="space-y-3">
              <div class="flex items-center gap-2.5">
                <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div class="h-3.5 w-44 bg-gray-100 dark:bg-gray-800 rounded" />
              </div>
              <div class="flex items-center gap-2.5">
                <div class="w-7 h-7 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                <div class="h-3.5 w-32 bg-gray-100 dark:bg-gray-800 rounded" />
              </div>
            </div>
          </div>

          <!-- About section -->
          <div>
            <div class="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div class="space-y-1.5">
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-full" />
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-5/6" />
              <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-3/4" />
            </div>
          </div>
        </div>

        <!-- BOTTOM: branding logo -->
        <div class="flex-shrink-0 border-t border-gray-100 dark:border-gray-700/60
                    hidden md:flex items-center px-5 py-3">
          <div class="h-6 w-24 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>

      </div>

      <!-- ── Right panel (3/5) ─────────────────────────────────────────── -->
      <!-- Inner content is constrained to md:w-10/12 lg:w-9/12 to match
           the actual CalendarPanel narrowing that makes the calendar compact -->
      <div class="hidden md:flex w-3/5 h-full">
        <div class="flex flex-col h-full md:w-10/12 lg:w-9/12 mx-auto pl-5">

          <!-- Section label + 24h toggle -->
          <div class="flex items-center justify-between pt-5 pb-3 pr-7 flex-shrink-0">
            <div class="h-3 w-28 bg-gray-200 dark:bg-gray-700 rounded" />
            <div class="flex items-center gap-2">
              <div class="h-5 w-9 bg-gray-200 dark:bg-gray-700 rounded-full" />
              <div class="h-3 w-6 bg-gray-100 dark:bg-gray-800 rounded" />
            </div>
          </div>

          <!-- Calendar month header (matches v-calendar top row) -->
          <div class="flex items-center justify-between mt-5 mb-3 pr-4">
            <div class="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
            <div class="flex gap-2">
              <div class="h-6 w-6 bg-gray-100 dark:bg-gray-800 rounded-full" />
              <div class="h-6 w-6 bg-gray-100 dark:bg-gray-800 rounded-full" />
            </div>
          </div>

          <!-- Day-of-week labels -->
          <div class="grid grid-cols-7 gap-1 mb-2 pr-4">
            <div v-for="i in 7" :key="i"
                 class="h-3 bg-gray-100 dark:bg-gray-800 rounded mx-auto w-5" />
          </div>

          <!-- Calendar day grid (5 weeks) — rounded-full day circles -->
          <div class="grid grid-cols-7 gap-y-1 pr-4">
            <div v-for="i in 35" :key="i"
                 class="mx-auto w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>

          <!-- Timezone selector — matches rounded-[5px] bg-gray-100 style -->
          <div class="mt-4 pr-8">
            <div class="h-3 w-14 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div class="h-11 rounded-[5px] bg-gray-100 dark:bg-gray-700/60 w-full" />
          </div>

        </div>
      </div>

    </div>
  </div>
</template>
